import { cleanTourTitle } from "@/lib/presentation-text";
import { useCurrency } from "@/lib/currency";
import { Link } from "@tanstack/react-router";
import { Clock, MapPin, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";

import { formatDuration, formatPrice, makeTourSlug } from "@/lib/slug";
import type { UnifiedTour } from "@/lib/catalog.functions";
import { TOUR_IMAGE_FALLBACK, handleTourImgError } from "@/components/site/tourImageFallback";
import { cardSummary, cancellationLabel, type CatalogBadge } from "@/lib/catalog-presentation";
import { TourCancellation } from "@/components/site/TourCancellation";
import { TourPriceContext } from "./TourPriceContext";
import { locationLabel } from "@/lib/location-label";
import { FinalTourPrice } from "./FinalTourPrice";
import { BuenFinBadge } from "./BuenFinCampaign";
import { cdnImage, cdnSrcSet, CARD_WIDTHS, CARD_SIZES } from "@/lib/image-cdn";

/**
 * Source-agnostic tour card. Renders Bokun and custom (Mostrando México)
 * tours identically — the link target is derived from `tour.source`.
 */
export function UnifiedTourCard({
  tour,
  priority = false,
  badge,
}: {
  tour: UnifiedTour;
  /** Ignored — kept for backward compatibility with callers. */
  badge?: CatalogBadge;
  priority?: boolean;
}) {
  useCurrency(); // subscribe: formatPrice reads the currency store
  const { t, i18n } = useTranslation();
  const slug = tour.source === "custom" ? tour.slug : makeTourSlug(tour.title, tour.id);
  const img = tour.images[0];
  const dur = formatDuration(tour.durationMinutes);
  const summary = cardSummary(tour.summary || tour.description);
  // No rating is rendered here: the unified catalog payload carries no review
  // data, so nothing is invented. Real ratings live on the tour detail page.

  return (
    <Link
      to="/tour/$slug"
      params={{ slug }}
      preload="intent"
      className="group flex h-full flex-col overflow-hidden rounded-[20px] bg-card shadow-[0_10px_30px_-12px_rgba(20,12,4,0.22)] ring-1 ring-border/50 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-18px_rgba(20,12,4,0.35)] hover:ring-primary/25"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        <img
          src={img ? cdnImage(img, 640, { height: 480 }) : TOUR_IMAGE_FALLBACK}
          srcSet={cdnSrcSet(img, CARD_WIDTHS, { aspect: 4 / 3 })}
          sizes={CARD_SIZES}
          alt={`${cleanTourTitle(tour.title)} – foto 1`}
          width={800}
          height={600}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={priority ? "high" : "auto"}
          onError={handleTourImgError}
          className="absolute inset-0 block h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.07]"
        />
        {!img && (
          <div className="pointer-events-none absolute inset-0 flex items-end justify-center pb-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {t("catalogPage.imageUnavailable")}
          </div>
        )}
        {/* One single promo badge: the discount logic is untouched, only the
            highest-value promotion is surfaced to avoid visual noise. */}
        {badge && (
          <div className="absolute left-3 top-3">
            <span className="inline-flex items-center gap-1 rounded-full metal-orange px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-md ring-1 ring-white/20">
              {t(`badges.${badge}`)}
            </span>
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/45 to-transparent" />
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4 sm:p-5">
        <BuenFinBadge id={tour.id} />
        {/* Jerarquía fija: destino → título → resumen → valor → duración/precio.
            Las alturas mínimas alinean precios y CTAs entre tarjetas vecinas
            sin recortar información esencial. */}
        <div className="flex min-h-[1.25rem] items-center gap-1 mm-meta text-muted-foreground">
          {tour.location && (
            <>
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="line-clamp-1">{locationLabel(tour.location, i18n.language)}</span>
            </>
          )}
        </div>
        <h3 className="line-clamp-2 min-h-[2.6em] font-display text-[17px] font-semibold leading-snug tracking-tight text-foreground sm:text-lg">
          {cleanTourTitle(tour.title)}
        </h3>
        {summary && (
          <p className="min-h-[2.5rem] text-sm leading-relaxed text-muted-foreground">
            {summary}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/[0.06] px-2 py-0.5 text-[11px] font-semibold text-primary">
            <Shield className="h-3 w-3" aria-hidden />
            <TourCancellation tour={tour} />
          </span>
        </div>
        <div className="mt-auto flex items-end justify-between gap-3 border-t border-border/60 pt-3">
          {dur ? (
            <span className="inline-flex items-center gap-1 mm-meta text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> {dur}
            </span>
          ) : (
            <span />
          )}
          <div className="shrink-0 text-right">
            {tour.source === "bokun" || tour.priceFrom != null ? (
              <>
                <div className="font-display text-xl font-bold leading-tight tracking-tight text-foreground">
                  {tour.source === "bokun" ? <FinalTourPrice id={tour.id} context={<TourPriceContext tour={tour} />}/> : formatPrice(tour.priceFrom, tour.currency)}
                </div>
                {tour.source !== "bokun" && <TourPriceContext tour={tour} />}
              </>
            ) : (
              /* No price in the source data — never render "Desde —". */
              <div className="text-[12.5px] font-semibold leading-tight text-primary">
                {t("homeUi.askAvailability")}
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
